import React, {useMemo, useState} from "react";
import {gql, useQuery} from "@apollo/client";
import {useTranslation} from "react-i18next";
import {Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Paper, Toolbar, Typography, useMediaQuery} from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import PersonIcon from "@mui/icons-material/Person";
import PlaceIcon from "@mui/icons-material/Place";
import DevicesIcon from "@mui/icons-material/Devices";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import {GoogleMap, InfoWindow, Marker} from "@react-google-maps/api";
import PageHeader from "../components/PageHeader";
import SearchField from "../components/SearchField";
import ResponsiveSelect from "../components/ResponsiveSelect";
import sortOptionsByLabel from "../utils/sortOptionsByLabel";

const GET_MAP_LOCATIONS = gql`
    query GetMapLocations {
        filterLocation(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
            type
            address {
                fullAddress
                latitude
                longitude
            }
            customer {
                id
                name
            }
        }
    }
`;

const GET_ACTIVE_ALARMS = gql`
    query GetActiveAlarms {
        paginateDeviceAlarm(
            pagination: { page: 0, size: 10000 }
            sortBy: [{ field: "lastSeen", direction: DESC }]
            criteria: { deleted: false }
        ) {
            content {
                id
                deviceType
                location
                locationName
            }
        }
    }
`;

const FILTER_CUSTOMERS = gql`
    query FilterCustomer {
        filterCustomer(criteria: { deleted: false }, sortBy: { field: "name", direction: ASC }) {
            id
            name
        }
    }
`;

const DEFAULT_CENTER = {lat: 39.9255, lng: 32.8663};

const GREEN_MARKER = "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
const RED_MARKER = "http://maps.google.com/mapfiles/ms/icons/red-dot.png";

export default function MapPage() {
    const {t} = useTranslation();
    const role = localStorage.getItem("role") || sessionStorage.getItem("role");
    const isSmall = useMediaQuery("(max-width:600px)");
    const [activeMarker, setActiveMarker] = useState(null);

    const [nameFilter, setNameFilter] = useState("");
    const [addressFilter, setAddressFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [customerFilter, setCustomerFilter] = useState("");
    const [appliedFilters, setAppliedFilters] = useState({name: "", address: "", type: "", customer: ""});

    const {data: locationData, loading: locLoading} = useQuery(GET_MAP_LOCATIONS);
    const {data: alarmData, loading: alarmLoading} = useQuery(GET_ACTIVE_ALARMS);
    const {data: customerData} = useQuery(FILTER_CUSTOMERS);

    const applyFilters = () => {
        setAppliedFilters({
            name: nameFilter.trim(),
            address: addressFilter.trim(),
            type: typeFilter,
            customer: customerFilter,
        });
        setActiveMarker(null);
    };

    const resetFilters = () => {
        setNameFilter("");
        setAddressFilter("");
        setTypeFilter("");
        setCustomerFilter("");
        setAppliedFilters({name: "", address: "", type: "", customer: ""});
        setActiveMarker(null);
    };

    const handleSearchKeyDown = (e) => {
        if (e.key === "Enter") applyFilters();
    };

    const alarmsByLocation = useMemo(() => {
        const map = {};
        const alarms = alarmData?.paginateDeviceAlarm?.content || [];
        alarms.forEach((alarm) => {
            if (!alarm.location) return;
            if (!map[alarm.location]) map[alarm.location] = {count: 0, deviceTypes: new Set()};
            map[alarm.location].count += 1;
            if (alarm.deviceType) map[alarm.location].deviceTypes.add(alarm.deviceType);
        });
        return map;
    }, [alarmData]);

    const allLocations = useMemo(() => {
        const raw = locationData?.filterLocation || [];
        return raw
            .filter((loc) => loc.address?.latitude && loc.address?.longitude)
            .map((loc) => ({
                id: loc.id,
                name: loc.name,
                type: loc.type,
                customerId: loc.customer?.id || "",
                customerName: loc.customer?.name || "-",
                address: loc.address?.fullAddress || "-",
                lat: loc.address.latitude,
                lng: loc.address.longitude,
                alarmCount: alarmsByLocation[loc.id]?.count || 0,
                deviceTypes: alarmsByLocation[loc.id]?.deviceTypes
                    ? Array.from(alarmsByLocation[loc.id].deviceTypes)
                    : [],
            }));
    }, [locationData, alarmsByLocation]);

    const filteredLocations = useMemo(() => {
        return allLocations.filter((loc) => {
            if (appliedFilters.name && !loc.name.toLowerCase().includes(appliedFilters.name.toLowerCase())) return false;
            if (appliedFilters.address && !loc.address.toLowerCase().includes(appliedFilters.address.toLowerCase())) return false;
            if (appliedFilters.type && loc.type !== appliedFilters.type) return false;
            if (appliedFilters.customer && loc.customerId !== appliedFilters.customer) return false;
            return true;
        });
    }, [allLocations, appliedFilters]);

    const stats = useMemo(() => {
        const total = filteredLocations.length;
        const withAlarm = filteredLocations.filter((l) => l.alarmCount > 0).length;
        return {total, withAlarm, withoutAlarm: total - withAlarm};
    }, [filteredLocations]);

    const locationOptions = sortOptionsByLabel([
        {value: "", label: t?.("common.all") || "All"},
        {value: "HOME", label: t("locationPage.types.home")},
        {value: "RESTAURANT", label: t("locationPage.types.restaurant")},
        {value: "CAFE", label: t("locationPage.types.cafe")},
        {value: "BAR", label: t("locationPage.types.bar")},
        {value: "OFFICE", label: t("locationPage.types.office")},
        {value: "SHOPPING_MALL", label: t("locationPage.types.shopping_mall")},
        {value: "OTHER", label: t("locationPage.types.other")},
    ], {preserveFirst: true});

    const loading = locLoading || alarmLoading;

    return (
        <Box>
            <PageHeader
                title={t("mapPage.title")}
                subtitle={t("mapPage.subtitle")}
                icon={<MapIcon/>}
            />

            <Paper elevation={3} sx={{borderRadius: 2, overflow: "hidden", mb: 2}}>
                <Toolbar
                    sx={{
                        px: 2,
                        py: 1,
                        gap: 1,
                        flexWrap: "wrap",
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        bgcolor: "background.paper",
                    }}
                >
                    <SearchField
                        label={t("locationPage.fields.name")}
                        value={nameFilter}
                        onChange={(e) => setNameFilter(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t("locationPage.search.namePlaceholder")}
                        isSmall={isSmall}
                    />

                    <SearchField
                        label={t("locationPage.fields.address")}
                        value={addressFilter}
                        onChange={(e) => setAddressFilter(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                        placeholder={t("locationPage.search.addressPlaceholder")}
                        isSmall={isSmall}
                    />

                    <ResponsiveSelect
                        label={t("locationPage.fields.type")}
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        onClear={() => setTypeFilter("")}
                        options={locationOptions}
                        t={t}
                    />

                    {role !== "CUSTOMER" && (
                        <ResponsiveSelect
                            label={t("locationPage.fields.customer")}
                            value={customerFilter}
                            onChange={(e) => setCustomerFilter(e.target.value)}
                            onClear={() => setCustomerFilter("")}
                            data={customerData}
                            dataKey="filterCustomer"
                            t={t}
                            isSmall={isSmall}
                        />
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon/>}
                        onClick={applyFilters}
                        sx={{height: 40, px: 2, textTransform: "none", fontWeight: 500, minWidth: isSmall ? "100%" : "auto"}}
                    >
                        {t("common.search")}
                    </Button>

                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<ClearIcon/>}
                        onClick={resetFilters}
                        sx={{height: 40, px: 2, textTransform: "none", fontWeight: 500, minWidth: isSmall ? "100%" : "auto"}}
                    >
                        {t("common.clear")}
                    </Button>
                </Toolbar>
            </Paper>

            <Box sx={{display: "flex", gap: 2, mb: 2, flexWrap: "wrap"}}>
                <StatCard
                    icon={<LocationOnIcon sx={{color: "primary.main"}}/>}
                    label={t("mapPage.totalLocations")}
                    value={stats.total}
                    color="primary.main"
                />
                <StatCard
                    icon={<WarningAmberIcon sx={{color: "error.main"}}/>}
                    label={t("mapPage.withAlarm")}
                    value={stats.withAlarm}
                    color="error.main"
                />
                <StatCard
                    icon={<CheckCircleOutlineIcon sx={{color: "success.main"}}/>}
                    label={t("mapPage.withoutAlarm")}
                    value={stats.withoutAlarm}
                    color="success.main"
                />
            </Box>

            <Card variant="outlined">
                <CardContent sx={{p: 0, "&:last-child": {pb: 0}}}>
                    {loading ? (
                        <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", height: "calc(100vh - 380px)"}}>
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <GoogleMap
                            mapContainerStyle={{width: "100%", height: "calc(100vh - 380px)"}}
                            center={DEFAULT_CENTER}
                            zoom={6}
                            options={{
                                disableDefaultUI: false,
                                streetViewControl: false,
                                mapTypeControl: true,
                            }}
                            onClick={() => setActiveMarker(null)}
                        >
                            {filteredLocations.map((loc) => (
                                <Marker
                                    key={loc.id}
                                    position={{lat: loc.lat, lng: loc.lng}}
                                    icon={loc.alarmCount > 0 ? RED_MARKER : GREEN_MARKER}
                                    onClick={() => setActiveMarker(loc)}
                                />
                            ))}

                            {activeMarker && (
                                <InfoWindow
                                    position={{lat: activeMarker.lat, lng: activeMarker.lng}}
                                    onCloseClick={() => setActiveMarker(null)}
                                    options={{maxWidth: 320}}
                                    onDomReady={() => {
                                        const btn = document.querySelector(".gm-ui-hover-effect");
                                        if (btn) btn.style.display = "none";
                                        const iwOuter = document.querySelector(".gm-style-iw-d");
                                        if (iwOuter) iwOuter.style.overflow = "hidden";
                                    }}
                                >
                                    <Box sx={{width: 260, overflow: "hidden"}}>
                                        {activeMarker.alarmCount > 0 && (
                                            <Box sx={{
                                                px: 1.5, py: 0.8,
                                                bgcolor: "error.main",
                                                mx: -0.5, mt: -0.5, mb: 1,
                                                borderRadius: "4px 4px 0 0",
                                            }}>
                                                <Typography variant="subtitle2" sx={{fontWeight: 700, color: "#fff", lineHeight: 1.3}}>
                                                    {activeMarker.name}
                                                </Typography>
                                            </Box>
                                        )}

                                        {activeMarker.alarmCount === 0 && (
                                            <Typography variant="subtitle2" sx={{fontWeight: 700, mb: 0.5, px: 0.5, pt: 0.5}}>
                                                {activeMarker.name}
                                            </Typography>
                                        )}

                                        <Box sx={{px: 0.5, pb: 0.5, display: "flex", flexDirection: "column", gap: 0.6}}>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 0.8}}>
                                                <PersonIcon sx={{fontSize: 16, color: "text.secondary", flexShrink: 0}}/>
                                                <Typography variant="body2" noWrap>{activeMarker.customerName}</Typography>
                                            </Box>

                                            <Box sx={{display: "flex", alignItems: "flex-start", gap: 0.8}}>
                                                <PlaceIcon sx={{fontSize: 16, color: "text.secondary", flexShrink: 0, mt: 0.2}}/>
                                                <Typography variant="body2" color="text.secondary" sx={{
                                                    display: "-webkit-box",
                                                    WebkitLineClamp: 2,
                                                    WebkitBoxOrient: "vertical",
                                                    overflow: "hidden",
                                                    wordBreak: "break-word",
                                                    lineHeight: 1.4,
                                                }}>
                                                    {activeMarker.address}
                                                </Typography>
                                            </Box>

                                            {activeMarker.deviceTypes.length > 0 && (
                                                <>
                                                    <Divider/>
                                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap"}}>
                                                        <DevicesIcon sx={{fontSize: 16, color: "text.secondary", flexShrink: 0}}/>
                                                        {activeMarker.deviceTypes.map((dt) => (
                                                            <Chip key={dt} label={dt} size="small" variant="outlined" sx={{height: 20, fontSize: "0.7rem"}}/>
                                                        ))}
                                                    </Box>
                                                </>
                                            )}

                                            {activeMarker.alarmCount > 0 && (
                                                <>
                                                    <Divider/>
                                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.8}}>
                                                        <NotificationsActiveIcon sx={{fontSize: 16, color: "error.main", flexShrink: 0}}/>
                                                        <Typography variant="body2" sx={{color: "error.main", fontWeight: 600}}>
                                                            {t("mapPage.activeAlarms")}: {activeMarker.alarmCount}
                                                        </Typography>
                                                    </Box>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </InfoWindow>
                            )}
                        </GoogleMap>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}

function StatCard({icon, label, value, color}) {
    return (
        <Card variant="outlined" sx={{minWidth: 160, flex: 1}}>
            <CardContent sx={{display: "flex", alignItems: "center", gap: 1.5, py: 1.5, "&:last-child": {pb: 1.5}}}>
                {icon}
                <Box>
                    <Typography variant="h6" sx={{fontWeight: 700, color, lineHeight: 1.2}}>
                        {value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {label}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}
